/**
 * Language switcher dropdown — pill-shaped, native-script labels, DPDP-friendly.
 *
 * Mounted in the landing navbar; also usable inside any dashboard.
 * Selecting a language updates localStorage + document <html lang> + adds a
 * `?lang=xx` query param to the URL so users can share localized links.
 */
import { useEffect, useRef, useState } from "react";
import { Globe, Check, ChevronDown } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";

export function LangSwitcher({ compact = false }) {
    const { lang, setLang, langs } = useLang();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const active = langs.find((l) => l.code === lang) || langs[0];

    return (
        <div ref={ref} className="relative" data-testid="lang-switcher">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-label="Change language"
                aria-expanded={open}
                data-testid="lang-switcher-trigger"
                title={`Language: ${active.native}`}
                className="inline-flex items-center gap-1.5 h-10 px-3 rounded-full border border-[#D1D5DB] bg-white hover:bg-[#F9FAFB] transition-colors font-semibold text-[13.5px] text-[#111827]"
            >
                <Globe className="h-4 w-4 text-[#374151]" />
                <span className="text-[13px] font-semibold capitalize tracking-wide text-[#111827]">{active.code.charAt(0).toUpperCase() + active.code.slice(1)}</span>
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div
                    role="menu"
                    data-testid="lang-switcher-menu"
                    className="absolute right-0 mt-2 w-56 rounded-2xl border border-[#E3E0D3] bg-white shadow-[0_20px_50px_rgba(15,51,17,0.15)] overflow-hidden z-50"
                >
                    <div className="max-h-80 overflow-y-auto py-1">
                        {langs.map((l) => {
                            const isActive = l.code === lang;
                            return (
                                <button
                                    key={l.code}
                                    type="button"
                                    onClick={() => { setLang(l.code); setOpen(false); }}
                                    data-testid={`lang-option-${l.code}`}
                                    className={`w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left text-[13.5px] hover:bg-[#F1F7ED] transition-colors ${
                                        isActive ? "bg-[#F1F7ED] text-[#1B5E20] font-bold" : "text-[#0F3311]"
                                    }`}
                                >
                                    <span className="flex-1 min-w-0">
                                        <span className="block truncate">{l.native}</span>
                                        <span className="block text-[11px] text-[#888] uppercase tracking-wider mt-0.5">{l.code}</span>
                                    </span>
                                    {isActive && <Check className="h-4 w-4 text-[#1B5E20] flex-shrink-0" />}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

export default LangSwitcher;
